"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getClientInitializationValues = void 0;
const types_1 = require("./types");
const fetcher_1 = require("./fetcher");
const clientError_1 = require("./fetcher/errors/clientError");
const responseError_1 = require("./fetcher/errors/responseError");
/**
* @since 1.0.0
* @async
* @static
* @param {ClientOptions} clientOptions
* @param {String} targetApp
* @param {Identifiers} identifiers
* @param {CustomAttributes} customAttributes
* @returns {FrontendExperimentsResponse | undefined}
* @throws {ClientError} {@link RequestErrorKind.BadRequestException}
* @throws {ClientError} {@link RequestErrorKind.UnknownException}
* @throws {ClientError} {@link RequestErrorKind.IllegalStateException}
* @throws {ResponseError} {@link RequestErrorKind.UnauthorisedRequestException}
* @throws {ResponseError} {@link RequestErrorKind.NotFoundException}
* @throws {ResponseError} {@link RequestErrorKind.NoContentException}
* @throws {ResponseError} {@link RequestErrorKind.BadResponseException}
* @throws {ResponseError} {@link RequestErrorKind.RateLimitedException}
* @summary This will fetch experiment values by calling out to feature-flag-service [fx3]{@link https://go.atlassian.com/fx3}
* @example
await FeatureGates.getClientInitializationValues(
{
            // This is an fx3 api key used to fetch the feature flag values.
            // Supported keys found at go/fx3/resources/api-keys
            apiKey: 'client-test',
            // This will be used to filter data to only a specific target app
            targetApp: 'jira_web',
       },
{
            // These are expected identifiers, you must provide them if they are relevant to your product and will be added to exposures events
            // At least one identifier must be provided
            analyticsAnonymousId: '<analyticsAnonymousId>',
            atlassianAccountId: '<aaid>',
            atlassianOrgId: '<orgid>',
            tenantId: '<tenantid>',
            transactionAccountId: '<transactionAccountId'>,
            trelloUserId: '<trelloUserId'>,
       },
{
            // These are additional custom attributes that can be used for targeting and will be added to exposure events
             exampleCustomAttribute: '<attributeValue>',
       }
);
* @example
A successful request will return a [FrontendExperimentsRequestResponse]{@link FrontendExperimentsRequestResponse}
{
        // The client sdk key
        clientSdkKey: string;
        // Experiment values
        experimentValues: Record<string, value>;
        // Custom attributes used to fetch experiment values. Includes Targeting + User Dimension TAP traits.
        customAttributes: Record<string, value>;
   }
*/
const getClientInitializationValues = async (clientOptions, targetApp, identifiers, customAttributes) => {
    const { apiKey } = clientOptions;
    try {
        validateRequest(identifiers);
        return await (0, fetcher_1.fetchExperimentValues)(apiKey, targetApp, identifiers, customAttributes);
    }
    catch (error) {
        throw handleError(error);
    }
};
exports.getClientInitializationValues = getClientInitializationValues;
const validateRequest = (identifiers) => {
    const noIdentifiers = Object.keys(identifiers).length == 0;
    if (noIdentifiers) {
        throw new clientError_1.ClientError('No request identifiers supplied.', types_1.RequestErrorKind.BadRequestException);
    }
};
const handleError = (error) => {
    const isResponseError = error instanceof responseError_1.ResponseError;
    const isClientError = error instanceof clientError_1.ClientError;
    if (isResponseError || isClientError) {
        return error;
    }
    return new clientError_1.ClientError('An unknown error occurred while trying to get initialization values for client.', types_1.RequestErrorKind.UnknownException);
};
